/**
 * @fileoverview Data Transformation Utilities
 * @description Functions to transform raw Copilot data for charts
 */

/**
 * Get daily user trends
 * @param {Array} data - Raw copilot data
 * @returns {Object} Transformed data for line chart
 */
export const getDailyUserTrends = (data) => {
  return data.map((day) => ({
    date: day.date,
    totalActiveUsers: day.total_active_users || 0,
    totalEngagedUsers: day.total_engaged_users || 0,
    ideChatUsers: day.copilot_ide_chat?.total_engaged_users || 0,
    codeCompletionUsers: day.copilot_ide_code_completions?.total_engaged_users || 0,
    dotcomChatUsers: day.copilot_dotcom_chat?.total_engaged_users || 0,
    prUsers: day.copilot_dotcom_pull_requests?.total_engaged_users || 0,
  }));
};

/**
 * Get editor usage distribution
 * @param {Array} data - Raw copilot data
 * @returns {Array} Aggregated editor data
 */
export const getEditorDistribution = (data) => {
  const editorMap = {};

  data.forEach((day) => {
    // IDE Chat editors
    day.copilot_ide_chat?.editors?.forEach((editor) => {
      if (!editorMap[editor.name]) {
        editorMap[editor.name] = { chatUsers: 0, completionUsers: 0, totalChats: 0 };
      }
      editorMap[editor.name].chatUsers += editor.total_engaged_users || 0;
      editor.models?.forEach((model) => {
        editorMap[editor.name].totalChats += model.total_chats || 0;
      });
    });

    // Code completion editors
    day.copilot_ide_code_completions?.editors?.forEach((editor) => {
      if (!editorMap[editor.name]) {
        editorMap[editor.name] = { chatUsers: 0, completionUsers: 0, totalChats: 0 };
      }
      editorMap[editor.name].completionUsers += editor.total_engaged_users || 0;
    });
  });

  return Object.entries(editorMap)
    .map(([name, stats]) => ({
      name,
      chatUsers: stats.chatUsers,
      completionUsers: stats.completionUsers,
      totalChats: stats.totalChats,
      totalUsers: stats.chatUsers + stats.completionUsers,
    }))
    .sort((a, b) => b.totalUsers - a.totalUsers);
};

/**
 * Get programming language usage
 * @param {Array} data - Raw copilot data
 * @returns {Array} Aggregated language data
 */
export const getLanguageDistribution = (data) => {
  const languageMap = {};

  data.forEach((day) => {
    day.copilot_ide_code_completions?.languages?.forEach((lang) => {
      if (!languageMap[lang.name]) {
        languageMap[lang.name] = {
          engagedUsers: 0,
          acceptances: 0,
          suggestions: 0,
          linesAccepted: 0,
          linesSuggested: 0,
        };
      }
      languageMap[lang.name].engagedUsers += lang.total_engaged_users || 0;
    });

    // Get detailed language stats from editor models
    day.copilot_ide_code_completions?.editors?.forEach((editor) => {
      editor.models?.forEach((model) => {
        model.languages?.forEach((lang) => {
          if (!languageMap[lang.name]) {
            languageMap[lang.name] = {
              engagedUsers: 0,
              acceptances: 0,
              suggestions: 0,
              linesAccepted: 0,
              linesSuggested: 0,
            };
          }
          languageMap[lang.name].acceptances += lang.total_code_acceptances || 0;
          languageMap[lang.name].suggestions += lang.total_code_suggestions || 0;
          languageMap[lang.name].linesAccepted += lang.total_code_lines_accepted || 0;
          languageMap[lang.name].linesSuggested += lang.total_code_lines_suggested || 0;
        });
      });
    });
  });

  return Object.entries(languageMap)
    .map(([name, stats]) => ({
      name,
      ...stats,
      acceptanceRate: stats.suggestions > 0 
        ? ((stats.acceptances / stats.suggestions) * 100).toFixed(1) 
        : 0,
    }))
    .filter((lang) => lang.engagedUsers > 0 || lang.acceptances > 0)
    .sort((a, b) => b.engagedUsers - a.engagedUsers);
};

/**
 * Get repository PR activity
 * @param {Array} data - Raw copilot data
 * @returns {Array} Aggregated repository data
 */
export const getRepositoryActivity = (data) => {
  const repoMap = {};

  data.forEach((day) => {
    day.copilot_dotcom_pull_requests?.repositories?.forEach((repo) => {
      if (!repo.name || repo.name === '') return;
      
      if (!repoMap[repo.name]) {
        repoMap[repo.name] = { engagedUsers: 0, prSummaries: 0 };
      }
      repoMap[repo.name].engagedUsers += repo.total_engaged_users || 0;
      repo.models?.forEach((model) => {
        repoMap[repo.name].prSummaries += model.total_pr_summaries_created || 0;
      });
    });
  });

  return Object.entries(repoMap)
    .map(([name, stats]) => ({
      name: name.replace('Alaska-ITS/', '').replace('alaskaair-IT/', ''),
      fullName: name,
      ...stats,
    }))
    .sort((a, b) => b.engagedUsers - a.engagedUsers);
    // Remove slice to let the component handle top N filtering
};

/**
 * Get weekly aggregated data
 * @param {Array} data - Raw copilot data
 * @returns {Array} Weekly aggregated data
 */
export const getWeeklyTrends = (data) => {
  const weekMap = {};

  data.forEach((day) => {
    const date = new Date(day.date);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekKey = weekStart.toISOString().split('T')[0];

    if (!weekMap[weekKey]) {
      weekMap[weekKey] = {
        week: weekKey,
        totalActiveUsers: 0,
        totalEngagedUsers: 0,
        ideChatUsers: 0,
        codeCompletionUsers: 0,
        days: 0,
      };
    }

    weekMap[weekKey].totalActiveUsers += day.total_active_users || 0;
    weekMap[weekKey].totalEngagedUsers += day.total_engaged_users || 0;
    weekMap[weekKey].ideChatUsers += day.copilot_ide_chat?.total_engaged_users || 0;
    weekMap[weekKey].codeCompletionUsers += day.copilot_ide_code_completions?.total_engaged_users || 0;
    weekMap[weekKey].days += 1;
  });

  return Object.values(weekMap)
    .map((week) => ({
      ...week,
      avgActiveUsers: Math.round(week.totalActiveUsers / week.days),
      avgEngagedUsers: Math.round(week.totalEngagedUsers / week.days),
    }))
    .sort((a, b) => new Date(a.week) - new Date(b.week));
};

/**
 * Get chat statistics over time
 * @param {Array} data - Raw copilot data
 * @returns {Array} Chat statistics
 */
export const getChatStatistics = (data) => {
  return data.map((day) => {
    let totalChats = 0;
    let totalCopyEvents = 0;
    let totalInsertionEvents = 0;

    day.copilot_ide_chat?.editors?.forEach((editor) => {
      editor.models?.forEach((model) => {
        totalChats += model.total_chats || 0;
        totalCopyEvents += model.total_chat_copy_events || 0;
        totalInsertionEvents += model.total_chat_insertion_events || 0;
      });
    });

    // Add dotcom chats
    day.copilot_dotcom_chat?.models?.forEach((model) => {
      totalChats += model.total_chats || 0;
    });

    return {
      date: day.date,
      totalChats,
      totalCopyEvents,
      totalInsertionEvents,
      dotcomChats: day.copilot_dotcom_chat?.models?.reduce((sum, m) => sum + (m.total_chats || 0), 0) || 0,
    };
  });
};

/**
 * Get code completion acceptance rates
 * @param {Array} data - Raw copilot data
 * @returns {Array} Acceptance rate data
 */
export const getAcceptanceRates = (data) => {
  return data.map((day) => {
    let totalAcceptances = 0;
    let totalSuggestions = 0;
    let totalLinesAccepted = 0;
    let totalLinesSuggested = 0;

    day.copilot_ide_code_completions?.editors?.forEach((editor) => {
      editor.models?.forEach((model) => {
        model.languages?.forEach((lang) => {
          totalAcceptances += lang.total_code_acceptances || 0;
          totalSuggestions += lang.total_code_suggestions || 0;
          totalLinesAccepted += lang.total_code_lines_accepted || 0;
          totalLinesSuggested += lang.total_code_lines_suggested || 0;
        });
      });
    });

    return {
      date: day.date,
      totalAcceptances,
      totalSuggestions,
      totalLinesAccepted,
      totalLinesSuggested,
      acceptanceRate: totalSuggestions > 0 
        ? ((totalAcceptances / totalSuggestions) * 100).toFixed(1) 
        : 0,
      lineAcceptanceRate: totalLinesSuggested > 0
        ? ((totalLinesAccepted / totalLinesSuggested) * 100).toFixed(1)
        : 0,
    };
  });
};

/**
 * Get summary statistics
 * @param {Array} data - Raw copilot data
 * @returns {Object} Summary statistics
 */
export const getSummaryStats = (data) => {
  const totals = {
    totalDays: data.length,
    totalActiveUsers: 0,
    totalEngagedUsers: 0,
    totalIdeChatUsers: 0,
    totalCodeCompletionUsers: 0,
    totalDotcomChatUsers: 0,
    totalPrUsers: 0,
    maxActiveUsers: 0,
    maxEngagedUsers: 0,
  };

  data.forEach((day) => {
    totals.totalActiveUsers += day.total_active_users || 0;
    totals.totalEngagedUsers += day.total_engaged_users || 0;
    totals.totalIdeChatUsers += day.copilot_ide_chat?.total_engaged_users || 0;
    totals.totalCodeCompletionUsers += day.copilot_ide_code_completions?.total_engaged_users || 0;
    totals.totalDotcomChatUsers += day.copilot_dotcom_chat?.total_engaged_users || 0;
    totals.totalPrUsers += day.copilot_dotcom_pull_requests?.total_engaged_users || 0;
    
    if ((day.total_active_users || 0) > totals.maxActiveUsers) {
      totals.maxActiveUsers = day.total_active_users;
    }
    if ((day.total_engaged_users || 0) > totals.maxEngagedUsers) {
      totals.maxEngagedUsers = day.total_engaged_users;
    }
  });

  return {
    ...totals,
    avgActiveUsers: Math.round(totals.totalActiveUsers / totals.totalDays),
    avgEngagedUsers: Math.round(totals.totalEngagedUsers / totals.totalDays),
    avgIdeChatUsers: Math.round(totals.totalIdeChatUsers / totals.totalDays),
    avgCodeCompletionUsers: Math.round(totals.totalCodeCompletionUsers / totals.totalDays),
  };
};
